import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import gqlPreAuth from "../../gql/preAuth";
import "./styles/approval-status-monitor.css";

const ROWS_PER_PAGE = 10;
const REFRESH_INTERVAL_MS = 3 * 60 * 1000;
const TABLE_COLUMN_COUNT = 8;

const pad = (value) => String(value).padStart(2, "0");

const getTodayDate = () => {
  const date = new Date();
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const titleCase = (value) => {
  if (!value) return "--";
  return value
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const normalizeDateInput = (value) => {
  if (!value) return null;
  const textValue = String(value).trim();
  const isoLike = textValue.match(
    /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(?:\.\d+)?(?:\s[+-]\d{4}|\sUTC|[+-]\d{2}:?\d{2})?/
  );

  if (isoLike) return new Date(`${isoLike[1]}T${isoLike[2]}`);
  return new Date(textValue);
};

const formatDateTime = (value, locale) => {
  const date = normalizeDateInput(value);
  if (!date || Number.isNaN(date.getTime())) return value || "--";

  return date.toLocaleTimeString(locale || [], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (value, locale) => {
  const date = normalizeDateInput(value);
  if (!date || Number.isNaN(date.getTime())) return value || "--";

  return date.toLocaleDateString(locale || "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getStatusClass = (status, outcome) => {
  const normalized = String(status || outcome || "").toLowerCase();
  if (["approved", "complete", "success", "not-required"].includes(normalized)) {
    return "is-approved";
  }
  if (
    ["rejected", "error", "abnormal request", "cancelled", "validation error"].includes(normalized)
  ) {
    return "is-rejected";
  }
  if (["pended", "pending", "queued"].includes(normalized)) {
    return "is-pending";
  }
  if (normalized.includes("partial")) return "is-partial";
  return "is-neutral";
};

const ApprovalStatusMonitor = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [now, setNow] = useState(new Date());
  const pageLoading = false;
  const today = useMemo(() => getTodayDate(), []);
  const monitorFilters = useMemo(
    () => ({
      from_date: today,
      to_date: today,
      exclude_status: ["validation_error"],
      limit: ROWS_PER_PAGE,
      page: 1,
    }),
    [today]
  );

  const { data, loading, error, refetch } = useQuery(
    gqlPreAuth.GET_PREAUTH_TRANSACTIONS,
    {
      variables: {
        input: monitorFilters,
      },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    }
  );

  const response = data?.listPreAuthTransactions;
  const rows = response?.data || [];
  // const total = Number(response?.total || 0);
  // const statusCounts = useMemo(
  //   () => response?.status_counts || [],
  //   [response?.status_counts]
  // );

  useEffect(() => {
    const clock = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(clock);
  }, []);

  useEffect(() => {
    const refreshTimer = setInterval(() => {
      refetch({ input: monitorFilters });
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(refreshTimer);
  }, [monitorFilters, refetch]);

  // const statusSummary = useMemo(() => {
  //   const counts = statusCounts.reduce((acc, item) => {
  //     acc[String(item.status || "").toLowerCase()] = item.count || 0;
  //     return acc;
  //   }, {});

  //   return [
  //     { label: t("Total Today"), value: total, className: "is-total" },
  //     {
  //       label: t("Approved"),
  //       value: (counts.approved || 0) + (counts["not-required"] || 0),
  //       className: "is-approved",
  //     },
  //     { label: t("Pending"), value: (counts.pended || 0) + (counts.pending || 0), className: "is-pending" },
  //     { label: t("Rejected"), value: (counts.rejected || 0) + (counts.cancelled || 0), className: "is-rejected" },
  //   ];
  // }, [statusCounts, total, t]);


  return (
    <main className="approval-monitor">
      <header className="approval-monitor__header">
        <div className="approval-monitor__title">
          <button
            type="button"
            className="approval-monitor__back"
            onClick={() => navigate(-1)}
            title={t("Go back")}
          >
            <ArrowBackIcon />
            <span>{t("Back")}</span>
          </button>
          <div>
          <h1>{t("Approval Monitor")}</h1>
          <span>{t("Same-day approval status with live refresh every 3 minutes")}</span>
          </div>
        </div>

        <div className="approval-monitor__clock">
          <AccessTimeIcon />
          <strong>
            {now.toLocaleTimeString(i18n.language || [], { hour: "2-digit", minute: "2-digit" })}
          </strong>
          <span>{now.toLocaleDateString(i18n.language || "en-GB", { weekday: "long", day: "2-digit", month: "short", year: "numeric" })}</span>
        </div>

        <div className="approval-monitor__actions">
          <button
            type="button"
            onClick={() => refetch({ input: monitorFilters })}
            disabled={loading || pageLoading}
            title={loading || pageLoading ? t("Refreshing") : t("Refresh")}
            className={loading || pageLoading ? "is-refreshing" : ""}
          >
            <RefreshIcon />
          </button>
        </div>
      </header>

      {/* <section className="approval-monitor__stats">
        {statusSummary.map((item) => (
          <div key={item.label} className={`approval-monitor__stat ${item.className}`}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </section> */}

      <section className="approval-monitor__table-wrap">
        {(loading || pageLoading) && rows.length > 0 && (
          <div className="approval-monitor__table-overlay">
            <RefreshIcon className="is-refreshing" />
            <span>{t("Refreshing table...")}</span>
          </div>
        )}
        {error ? (
          <div className="approval-monitor__state">
            {t("Unable to load approval monitor data.")}
          </div>
        ) : (
          <table className="approval-monitor__table">
            <colgroup>
              <col className="approval-monitor__col-index" />
              <col className="approval-monitor__col-document" />
              <col className="approval-monitor__col-name" />
              <col className="approval-monitor__col-company" />
              <col className="approval-monitor__col-type" />
              <col className="approval-monitor__col-request-date" />
              <col className="approval-monitor__col-status" />
              <col className="approval-monitor__col-response-time" />
            </colgroup>
            <thead>
              <tr>
                <th>#</th>
                <th>{t("Document ID")}</th>
                <th>{t("Name")}</th>
                <th>{t("Company")}</th>
                <th>{t("Type")}</th>
                <th>{t("Request Date")}</th>
                <th>{t("Status")}</th>
                <th>{t("Response Time")}</th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                rows.length === 0 &&
                Array.from({ length: ROWS_PER_PAGE }).map((_, rowIndex) => (
                  <tr
                    className="approval-monitor__skeleton-row"
                    key={`skeleton-${rowIndex}`}
                  >
                    {Array.from({ length: TABLE_COLUMN_COUNT }).map(
                      (__, cellIndex) => (
                        <td key={`skeleton-${rowIndex}-${cellIndex}`}>
                          <span />
                        </td>
                      )
                    )}
                  </tr>
                ))}
              {rows.map((row, index) => {
                const statusClass = getStatusClass(row.status, row.outcome);

                return (
                <tr
                  className={`approval-monitor__row ${statusClass}`}
                  key={row.transaction_id || `${monitorFilters.page}-${index}`}
                >
                  <td>
                    {(monitorFilters.page - 1) * ROWS_PER_PAGE + index + 1}
                  </td>
                  <td
                    className="approval-monitor__document-id"
                    title={row.patient_document_id || row.document_id || undefined}
                  >
                    {row.patient_document_id || row.document_id || "--"}
                  </td>
                  <td>{titleCase(row.patient_name)}</td>
                  <td title={row.payer_name || undefined}>{row.payer_name || "--"}</td>
                  <td>{t(titleCase(row.type))}</td>
                  <td>
                    <strong>{formatDate(row.date_ordered, i18n.language)}</strong>
                    {/* <span>{formatDate(row.date_ordered, i18n.language)}</span> */}
                  </td>
                  <td>
                    <span className={`approval-monitor__status-text ${statusClass}`}>
                      {t(titleCase(row.status || row.outcome))}
                    </span>
                  </td>
                  <td>
                    <strong>{formatDateTime(row.response_date, i18n.language)}</strong>
                    <span>{formatDate(row.response_date, i18n.language)}</span>
                  </td>
                </tr>
                );
              })}
              {!loading && rows.length === 0 && (
                <tr className="approval-monitor__empty-row">
                  <td colSpan={TABLE_COLUMN_COUNT}>
                    <div className="approval-monitor__empty-state">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" aria-hidden="true">
                        <rect x="8" y="14" width="48" height="36" rx="4" stroke="#d0ddea" strokeWidth="2.5" fill="#f6f9fc"/>
                        <rect x="16" y="22" width="32" height="3" rx="1.5" fill="#d0ddea"/>
                        <rect x="16" y="30" width="20" height="3" rx="1.5" fill="#e4edf5"/>
                        <rect x="16" y="38" width="26" height="3" rx="1.5" fill="#e4edf5"/>
                      </svg>
                      <p>{t("No same-day approval data found.")}</p>
                      <span>{t("Transactions submitted today will appear here automatically.")}</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </section>

      {/* {pageLoading && (
        <div className="approval-monitor__page-loading">
          Loading page {monitorFilters.page}...
        </div>
      )} */}
    </main>
  );
};

export default ApprovalStatusMonitor;
